import { useMemo, useState } from "react";
import { ClipboardList, Settings, LineChart } from "lucide-react";
import { Page } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { ContentCard } from "@/components/ui/ContentCard";
import { cn } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DistrictConfigPanel, DistrictConfiguration } from "./DistrictConfig";
import { IncidentAnalytics } from "./IncidentAnalytics";
import { IncidentList } from "./IncidentList";
import { IncidentPatternsDashboard } from "./IncidentPatternsDashboard";
import { Incident, IncidentStatus } from "./types";

const INITIAL_VISIBLE = 5;

type ViewMode = "mine" | "school" | "insights";

type Props = {
  incidents: Incident[];
  schoolIncidents: Incident[];
  onNewIncident: () => void;
  onSelectIncident: (id: string) => void;
  onSelectSchoolIncident: (id: string) => void;
  onDeleteIncident: (id: string) => void;
  onStatusChange?: (id: string, status: IncidentStatus) => void;
  onResetAll: () => void;
  statusFilter: IncidentStatus | "all";
  onStatusFilterChange: (filter: IncidentStatus | "all") => void;
  districtConfig: DistrictConfiguration;
  onSaveDistrictConfig: (config: DistrictConfiguration) => void;
  schoolId: string | null;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
};

export const IncidentDashboard = ({
  incidents,
  schoolIncidents,
  onNewIncident,
  onSelectIncident,
  onSelectSchoolIncident,
  onDeleteIncident,
  onStatusChange,
  onResetAll,
  statusFilter,
  onStatusFilterChange,
  districtConfig,
  onSaveDistrictConfig,
  schoolId,
  viewMode,
  onViewModeChange,
}: Props) => {
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [incidentToDeleteId, setIncidentToDeleteId] = useState<string | null>(null);
  const [showDistrictConfig, setShowDistrictConfig] = useState(false);
  const [showAllIncidents, setShowAllIncidents] = useState(false);
  const [showAllSchoolIncidents, setShowAllSchoolIncidents] = useState(false);

  const sortedFilteredIncidents = useMemo(() => {
    const filtered =
      statusFilter === "all" ? incidents : incidents.filter((i) => i.status === statusFilter);
    return [...filtered].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }, [incidents, statusFilter]);

  const visibleIncidents = showAllIncidents
    ? sortedFilteredIncidents
    : sortedFilteredIncidents.slice(0, INITIAL_VISIBLE);
  const hasMore = sortedFilteredIncidents.length > INITIAL_VISIBLE;

  const sortedSchoolIncidents = useMemo(() => {
    return [...schoolIncidents].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }, [schoolIncidents]);

  const visibleSchoolIncidents = showAllSchoolIncidents
    ? sortedSchoolIncidents
    : sortedSchoolIncidents.slice(0, INITIAL_VISIBLE);
  const hasMoreSchool = sortedSchoolIncidents.length > INITIAL_VISIBLE;

  const analytics = useMemo(() => {
    const total = incidents.length;
    const closed = incidents.filter((i) => i.status === "closed").length;
    const open = total - closed;
    const byGrade: Record<string, number> = {};
    const byPath: Record<string, number> = {};
    const last30 = incidents.filter(
      (i) => new Date(i.created_at).getTime() > Date.now() - 30 * 24 * 60 * 60 * 1000
    ).length;
    incidents.forEach((i) => {
      // Skip drafts that haven't captured a grade yet — otherwise the
      // analytics panel sprouts a ghost "Grade " row with no value.
      if (i.grade_level) {
        byGrade[i.grade_level] = (byGrade[i.grade_level] || 0) + 1;
      }
      if (i.chosen_path) {
        const pathLabel =
          i.chosen_path === "one_to_one_conversation"
            ? "1-on-1 Conversation"
            : i.chosen_path === "supported_meeting"
              ? "Supported Meeting"
              : i.chosen_path === "pause_support"
                ? "Pause & Support"
                : "Documentation Only";
        byPath[pathLabel] = (byPath[pathLabel] || 0) + 1;
      }
    });
    const resolutionRate = total > 0 ? Math.round((closed / total) * 100) : 0;
    const avgPerMonth =
      total > 0 && incidents.length > 0
        ? Math.round(
            total /
              Math.max(
                1,
                Math.ceil(
                  (Date.now() -
                    Math.min(...incidents.map((i) => new Date(i.created_at).getTime()))) /
                    (30 * 24 * 60 * 60 * 1000)
                )
              )
          )
        : 0;
    const parentContactCount = incidents.filter((i) => !!i.parent_contact_type).length;
    return { total, closed, open, last30, resolutionRate, byGrade, byPath, avgPerMonth, parentContactCount };
  }, [incidents]);

  return (
    <>
      <Page.Container>
        <Page.Header className="text-left flex flex-row flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold text-white font-literata">Behavior Incident Toolkit</h1>
            <p className="text-white/80 mt-2">Document, respond to, and resolve student behavior incidents — from office referral to re-entry.</p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Button
              className="bg-bodhi-teal text-white hover:bg-bodhi-teal/90"
              onClick={onNewIncident}
            >
              + New Incident
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="text-white border-white/40 bg-white/10 hover:bg-white/20"
              onClick={() => setShowDistrictConfig((v) => !v)}
            >
              <Settings className="w-4 h-4 mr-1" />
              Config
            </Button>
            {incidents.length > 0 && viewMode === "mine" && (
              <Button
                size="sm"
                className="bg-bodhi-gold text-white hover:bg-bodhi-gold/90 border-0"
                onClick={() => setShowResetConfirm(true)}
              >
                Clear All
              </Button>
            )}
          </div>
        </Page.Header>

        <Page.Content>
          {showDistrictConfig && (
            <div className="mb-6">
              <DistrictConfigPanel
                config={districtConfig}
                onSave={onSaveDistrictConfig}
                onClose={() => setShowDistrictConfig(false)}
              />
            </div>
          )}

          <div className="flex gap-1 mb-6 p-1 bg-white/10 rounded-xl w-fit flex-wrap">
            <button
              onClick={() => onViewModeChange("mine")}
              className={cn(
                "px-4 py-1.5 rounded-lg text-sm font-medium transition-colors",
                viewMode === "mine"
                  ? "bg-white text-bodhi-teal shadow-sm"
                  : "text-white/80 hover:text-white"
              )}
            >
              My Incidents
            </button>
            {schoolId && (
              <button
                onClick={() => onViewModeChange("school")}
                className={cn(
                  "px-4 py-1.5 rounded-lg text-sm font-medium transition-colors",
                  viewMode === "school"
                    ? "bg-white text-bodhi-teal shadow-sm"
                    : "text-white/80 hover:text-white"
                )}
              >
                School Incidents
              </button>
            )}
            <button
              onClick={() => onViewModeChange("insights")}
              className={cn(
                "px-4 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5",
                viewMode === "insights"
                  ? "bg-white text-bodhi-teal shadow-sm"
                  : "text-white/80 hover:text-white"
              )}
            >
              <LineChart className="w-3.5 h-3.5" />
              Pattern Insights
            </button>
          </div>

          {/* My Incidents view */}
          {viewMode === "mine" && (
            <>
              {incidents.length === 0 ? (
                <ContentCard>
                  <div className="py-8 md:py-10 max-w-3xl mx-auto">
                    <div className="flex items-start gap-4 mb-6">
                      <div className="w-12 h-12 bg-bodhi-teal/10 rounded-xl flex items-center justify-center flex-shrink-0">
                        <ClipboardList className="w-6 h-6 text-bodhi-teal" aria-hidden />
                      </div>
                      <div>
                        <h2 className="text-xl font-semibold text-[#248f84] font-literata mb-1">
                          Welcome to the Behavior Incident Toolkit
                        </h2>
                        <p className="text-sm text-gray-600 leading-relaxed">
                          A structured workflow for handling student behavior incidents end-to-end —
                          capturing what happened, guiding the restorative conversation, and producing
                          the documentation your district requires.
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="w-6 h-6 rounded-full bg-bodhi-teal text-white text-xs font-bold flex items-center justify-center flex-shrink-0">1</span>
                          <p className="text-sm font-semibold text-gray-800">Use this when</p>
                        </div>
                        <p className="text-xs text-gray-600 leading-relaxed">
                          A student is sent to your office, a behavior needs formal documentation,
                          or you're preparing for a restorative conversation with a student or family.
                        </p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="w-6 h-6 rounded-full bg-bodhi-teal text-white text-xs font-bold flex items-center justify-center flex-shrink-0">2</span>
                          <p className="text-sm font-semibold text-gray-800">What you'll do</p>
                        </div>
                        <p className="text-xs text-gray-600 leading-relaxed">
                          Log the situation, surface what's underneath, plan the conversation,
                          and record the resolution — with in-the-moment AI coaching at every step.
                        </p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="w-6 h-6 rounded-full bg-bodhi-teal text-white text-xs font-bold flex items-center justify-center flex-shrink-0">3</span>
                          <p className="text-sm font-semibold text-gray-800">What you'll walk out with</p>
                        </div>
                        <p className="text-xs text-gray-600 leading-relaxed">
                          A conversation guide, parent/guardian communication, a due-process record,
                          and a complete incident packet — ready to share, file, or enter into your SIS.
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-col items-center gap-2">
                      <Button
                        className="bg-bodhi-teal text-white hover:bg-bodhi-teal/90 px-8 py-3 text-base"
                        onClick={onNewIncident}
                      >
                        + Start a New Incident
                      </Button>
                      <p className="text-xs text-gray-400">Takes about 2 minutes to log the basics.</p>
                    </div>
                  </div>
                </ContentCard>
              ) : (
                <>
                  <IncidentAnalytics analytics={analytics} />

                  {/* Incident List */}
                  <ContentCard>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                      <h2 className="text-xl lg:text-2xl font-semibold text-[#248f84] font-literata">
                        Incidents
                      </h2>
                      <div className="flex flex-wrap gap-2">
                        {(["all", "intake", "coaching", "followup", "paused", "closed"] as const).map((s) => (
                          <button
                            key={s}
                            onClick={() => {
                              onStatusFilterChange(s);
                              setShowAllIncidents(false);
                            }}
                            className={cn(
                              "px-3 py-1.5 rounded-full text-xs font-medium transition-colors",
                              statusFilter === s
                                ? "bg-bodhi-teal text-white"
                                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                            )}
                          >
                            {s === "all"
                              ? "All"
                              : s === "followup"
                                ? "Follow-up"
                                : s === "paused"
                                  ? "Paused"
                                  : s.charAt(0).toUpperCase() + s.slice(1)}
                            {s !== "all" && (
                              <span className="ml-1 opacity-70">
                                ({incidents.filter((i) => i.status === s).length})
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>

                    <IncidentList
                      incidents={visibleIncidents}
                      onSelect={onSelectIncident}
                      onDelete={(id) => setIncidentToDeleteId(id)}
                      onStatusChange={onStatusChange}
                      showAll={showAllIncidents}
                      onToggleShowAll={() => setShowAllIncidents((v) => !v)}
                      hasMore={hasMore}
                    />
                  </ContentCard>
                </>
              )}
            </>
          )}

          {/* School Incidents view */}
          {viewMode === "school" && (
            <ContentCard>
              <div className="mb-4">
                <h2 className="text-xl lg:text-2xl font-semibold text-[#248f84] font-literata">
                  School Incidents
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Incidents created by colleagues at your school. View only.
                </p>
              </div>
              {sortedSchoolIncidents.length === 0 ? (
                <p className="text-sm text-gray-400 py-6 text-center">
                  No incidents from colleagues yet.
                </p>
              ) : (
                <IncidentList
                  incidents={visibleSchoolIncidents}
                  onSelect={onSelectSchoolIncident}
                  showAll={showAllSchoolIncidents}
                  onToggleShowAll={() => setShowAllSchoolIncidents((v) => !v)}
                  hasMore={hasMoreSchool}
                  showCreator
                />
              )}
            </ContentCard>
          )}
          {/* Pattern Insights view */}
          {viewMode === "insights" && (
            <IncidentPatternsDashboard
              incidents={incidents}
              schoolIncidents={schoolIncidents}
              schoolId={schoolId}
            />
          )}
        </Page.Content>
      </Page.Container>

      <AlertDialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear all incidents?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all incident records from this device. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-bodhi-gold text-white hover:bg-bodhi-gold/90"
              onClick={() => {
                onResetAll();
                setShowResetConfirm(false);
              }}
            >
              Clear all
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!incidentToDeleteId}
        onOpenChange={(open) => !open && setIncidentToDeleteId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this incident?</AlertDialogTitle>
            <AlertDialogDescription>
              This incident will be permanently removed from the list. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-bodhi-teal text-white hover:bg-bodhi-teal/90"
              onClick={() => {
                if (incidentToDeleteId) {
                  onDeleteIncident(incidentToDeleteId);
                  setIncidentToDeleteId(null);
                }
              }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
